import React, { useEffect, useMemo } from 'react';
import { Sparkle, Star, X } from '@phosphor-icons/react';
import { useAuthStore } from '../../stores/authStore';
import { useLocaleStore } from '../../stores/localeStore';
import { Locale } from '../../types';

const FIREWORK_PARTICLES = Array.from({ length: 24 }, (_, index) => index);
const STAR_PARTICLES = Array.from({ length: 14 }, (_, index) => index);

function ordinal(value: number, locale: Locale): string {
  if (locale === 'uk') return `${value}-й`;
  if (locale === 'es') return `${value}.º`;
  if (locale === 'fr') return value === 1 ? '1re' : `${value}e`;
  if (locale === 'no') return `${value}.`;
  if (locale === 'ro') return `a ${value}-a`;
  if (locale === 'nl') return `${value}e`;
  const lastTwo = value % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${value}th`;
  if (value % 10 === 1) return `${value}st`;
  if (value % 10 === 2) return `${value}nd`;
  if (value % 10 === 3) return `${value}rd`;
  return `${value}th`;
}

function countryName(countryCode: string | null, locale: Locale): string | null {
  if (!countryCode) return null;
  try {
    const DisplayNames = (Intl as unknown as { DisplayNames?: new (locales: string[], options: { type: 'region' }) => { of: (code: string) => string | undefined } }).DisplayNames;
    return DisplayNames ? new DisplayNames([locale], { type: 'region' }).of(countryCode) || countryCode : countryCode;
  } catch {
    return countryCode;
  }
}

const RegistrationCelebration: React.FC = () => {
  const celebration = useAuthStore((state) => state.registrationCelebration);
  const dismiss = useAuthStore((state) => state.dismissRegistrationCelebration);
  const locale = useLocaleStore((state) => state.locale);
  const detectedCountry = useMemo(() => countryName(celebration?.countryCode ?? null, locale), [celebration?.countryCode, locale]);

  useEffect(() => {
    if (!celebration) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [celebration, dismiss]);

  if (!celebration) return null;

  return (
    <div className="registration-celebration fixed inset-0 z-[4000] grid place-items-center overflow-hidden bg-slate-950/80 p-4 backdrop-blur-md" role="dialog" aria-modal="true" aria-labelledby="registration-celebration-title">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        {FIREWORK_PARTICLES.map((particle) => <i key={`firework-${particle}`} className="celebration-firework" style={{ '--firework-angle': `${(particle % 12) * 30}deg`, '--firework-left': particle < 12 ? '20%' : '80%', '--firework-color': `hsl(${particle * 31} 95% 68%)`, '--delay': `${(particle % 12) * -80}ms` } as React.CSSProperties} />)}
        {STAR_PARTICLES.map((particle) => {
          const angle = (particle / STAR_PARTICLES.length) * Math.PI * 2;
          return <Star key={`star-${particle}`} weight="fill" className="celebration-star" style={{ '--star-left': `${50 + Math.cos(angle) * 43}%`, '--star-top': `${50 + Math.sin(angle) * 39}%`, '--star-size': `${12 + (particle % 4) * 4}px`, '--delay': `${particle * -90}ms` } as React.CSSProperties} />;
        })}
      </div>

      <section className="celebration-card relative w-full max-w-lg overflow-hidden rounded-[36px] border border-yellow-200/50 bg-gradient-to-br from-violet-700 via-fuchsia-600 to-rose-500 p-[1px] text-center shadow-[0_30px_120px_rgba(217,70,239,0.55)]">
        <div className="relative overflow-hidden rounded-[35px] bg-slate-950/75 px-6 py-9 text-white backdrop-blur-xl sm:px-10 sm:py-11">
          <button type="button" onClick={dismiss} aria-label="Close congratulations" className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white/75 transition hover:rotate-6 hover:bg-white/20 hover:text-white">
            <X size={19} weight="bold" />
          </button>

          <div className="celebration-badge mx-auto grid h-24 w-24 place-items-center rounded-full bg-gradient-to-br from-yellow-200 via-amber-400 to-pink-500 shadow-[0_0_55px_rgba(250,204,21,0.65)]">
            <Star size={48} weight="fill" className="text-white drop-shadow-lg" />
          </div>
          <div className="mt-6 flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.28em] text-yellow-200"><Sparkle weight="fill" /> Welcome to mapbl <Sparkle weight="fill" /></div>
          <h2 id="registration-celebration-title" className="mt-3 text-4xl font-black tracking-tight sm:text-5xl">You made history!</h2>
          <p className="mx-auto mt-4 max-w-sm text-base leading-7 text-slate-200 sm:text-lg">
            You’re the <strong className="text-xl font-black text-yellow-300 sm:text-2xl">{ordinal(celebration.countryRank, locale)}</strong> person {detectedCountry ? <>in <strong className="text-white">{detectedCountry}</strong></> : 'worldwide'} to join mapbl.
          </p>
          <button type="button" onClick={dismiss} className="mt-7 rounded-2xl bg-gradient-to-r from-yellow-300 via-amber-400 to-pink-500 px-7 py-3.5 font-black text-slate-950 shadow-[0_12px_35px_rgba(251,191,36,0.35)] transition hover:scale-105 hover:brightness-110 active:scale-95">Let’s explore the map</button>
        </div>
      </section>
    </div>
  );
};

export default RegistrationCelebration;
